import { CheckIcon } from "lucide-react";
import type { DeliveryState } from "./portal-flow";

// The four stepper-circle states, shared by BOTH portal steppers (Schoolgegevens +
// Programmaselectie) so the two read IDENTICALLY:
//   green  = complete AND delivered (aangeleverd, unchanged)
//   blue   = complete AND NOT delivered (ingevuld, nog niet aangeleverd) — a PRIMARY check
//   dot    = started-but-incomplete OR delivery 'changed' (gewijzigd na een eerdere aanlevering)
//   ring   = not started (empty)
export type StepIconKind = "green" | "blue" | "dot" | "ring";

/** Map a (complete, deliveryState, started) triple onto a stepper-circle kind. `complete` rolls in
 *  the section's completeness; the rest follows the shared green/blue/dot/ring rule.
 *  'changed' (gewijzigd na een eerdere aanlevering) is always a dot, never a check — it must read as
 *  "iets staat open" until it is aangeleverd again. */
export function stepIconKind(complete: boolean, ds: DeliveryState | undefined, started: boolean): StepIconKind {
  if (complete && ds === "delivered") return "green";
  if (ds === "changed") return "dot";
  if (complete) return "blue";
  if (started) return "dot";
  return "ring";
}

/** Render one stepper circle for a given kind. `active`/`canOpen` only affect the empty ring
 *  (primary outline when this is the active or openable step). `dim` lightens a non-active circle
 *  so the active item stands out (subtle, not hard grey). Shared by both steppers' parent rows AND
 *  the nested sub-nav rows so they all read identically.
 *
 *  `changed` disambiguates the (o) dot, which otherwise means BOTH "deels ingevuld / nog niet
 *  compleet" AND "gewijzigd na een eerdere aanlevering". When the dot stands for a change
 *  (changed === true) its tooltip reads "Gewijzigd, opnieuw controleren"; otherwise "Nog niet
 *  compleet". So the bare dot is never relied on alone. */
export function StepCircle({
  kind,
  active,
  canOpen,
  dim,
  changed,
}: {
  kind: StepIconKind;
  active?: boolean;
  canOpen?: boolean;
  dim?: boolean;
  changed?: boolean;
}) {
  // Dim only non-active, non-empty circles; the active one (and empty rings, already faint) stay
  // full-strength so the current step keeps full emphasis.
  const dimmed = dim && !active;
  if (kind === "green") {
    return (
      <span
        className={`flex size-6 items-center justify-center rounded-full bg-emerald-600 ${dimmed ? "opacity-60" : ""}`}
        title="Aangeleverd"
      >
        <CheckIcon className="text-white" size={14} />
      </span>
    );
  }
  if (kind === "blue") {
    return (
      <span
        className={`flex size-6 items-center justify-center rounded-full bg-primary ${dimmed ? "opacity-60" : ""}`}
        title="Ingevuld, nog niet aangeleverd"
      >
        <CheckIcon className="text-white" size={14} />
      </span>
    );
  }
  if (kind === "dot") {
    return (
      <span
        className={`flex size-6 items-center justify-center rounded-full border-2 border-primary bg-primary/10 ${dimmed ? "opacity-60" : ""}`}
        title={changed ? "Gewijzigd, opnieuw controleren" : "Nog niet compleet"}
      >
        <span className="size-2 rounded-full bg-primary" />
      </span>
    );
  }
  // Empty ring: a primary outline when active/openable, a muted outline otherwise.
  return <span className={`flex size-6 items-center justify-center rounded-full border-2 ${active || canOpen ? "border-primary/40" : "border-border"} bg-background`} />;
}
